import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../../common/reducers/initContext";
import { initMapFactory, initMultiMapFactory, queryMapFactory, DefaultFailureAction, DefaultSuccessAction, dataHookWithCredentialsFactory } from "../../common/reducers/utilities";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { dateRangeOverlap, maxDate, minDate, parseDate } from "../../common/utils/dateUtils";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const key = a => a.id;

const prepareArray = data => ensureArray(data).map(availability => ({
    ...availability,
    hours: ((parseDate(availability.end) - parseDate(availability.start)) / (1000 * 60 * 60))
}))

export const availabilityMgrSlice = createSlice({
    name: 'availability',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            action.payload.ids.forEach(id => delete state.data[id]);
            prepareArray(action.payload.data).forEach(a => state.data[key(a)] = a);
        },
        addOrUpdate: (state, action) => {
            prepareArray(action.payload).forEach(a => state.data[key(a)] = a);
        },
        remove: (state, action) => {
            prepareArray(action.payload).forEach(a => delete state.data[key(a)])
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => availabilityMgrSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'shiftAvailability', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'shiftAvailability');

const fetchAvailability = aggregatingFetch(
    id => network => network.post('/api/v2/memberAvailabilities', [id],  data => availabilityMgrSlice.actions.set({ ids: [id], data }), visualizeFailure('get')),
    ids => network => network.post('/api/v2/memberAvailabilities', ids, data => availabilityMgrSlice.actions.set({ ids, data }), visualizeFailure('get')),
    { primitiveKey: true }
);
const fetchAvailabilitiesInRange = (start, end) => network => network.get('/api/v2/availabilities/' + encodeURIComponent(start) + '/' + encodeURIComponent(end), availabilityMgrSlice.actions.addOrUpdate, visualizeFailure('get'));

const fetchMemberAvailabilitiesInRange = aggregatingFetch(
    (memberid, start, end) => network => network.get(`/api/v2/memberAvailabilities/${encodeURIComponent(memberid)}/${encodeURIComponent(start)}/${encodeURIComponent(end)}`, availabilityMgrSlice.actions.addOrUpdate, visualizeFailure('get')),
    keys => network => network.get('/api/v2/availabilities/' + encodeURIComponent(minDate(keys, 1)) + '/' + encodeURIComponent(maxDate(keys, 2)), availabilityMgrSlice.actions.addOrUpdate, visualizeFailure('get'))
);

export const insertAvailability = (network, data) => network.post('/api/v1/memberAvailability', data, availabilityMgrSlice.actions.addOrUpdate, visualizeFailure('create'));
export const updateAvailability = (network, data) => network.put('/api/v1/memberAvailability', data, availabilityMgrSlice.actions.addOrUpdate, visualizeFailure('update'));
export const removeAvailability = (network, data) => network.delete(`/api/v1/memberAvailability/${encodeURIComponent(data.id)}`, availabilityMgrSlice.actions.remove, visualizeFailure('remove'));

// #endregion

// #region selectors
const selectAvailabilityData = state => state.availability.data
const selectAvailability = id => createSelector([selectAvailabilityData], availabilities => availabilities[id]);
const selectAvailabilities = ids => createSelector([selectAvailabilityData], availabilities => ids.reduce((res, id) => { if (availabilities[id]) { res.push(availabilities[id]) } return res }, []));

const selectAvailabilityInRange = (start, end) => createSelector([selectAvailabilityData], availabilities => Object.values(availabilities).filter(a => dateRangeOverlap(a, { start, end })));
const selectMemberAvailabilityInRange = (memberid, start, end) => createSelector([selectAvailabilityData], availabilites => Object.values(availabilites).filter(a => a.memberid === memberid && dateRangeOverlap(a, { start, end })));
// #endregion

const listen = {
    availabilityAdminCreated: (availability, { dispatch, websocket }) => {
        markFetched(websocket, 'availability', key(availability));
        dispatch(availabilityMgrSlice.actions.addOrUpdate(availability));
    },
    availabilityAdminUpdated: (availability, { dispatch }) => dispatch(availabilityMgrSlice.actions.addOrUpdate(availability)),
    availabilityAdminRemoved: (availability, { dispatch }) => dispatch(availabilityMgrSlice.actions.remove(availability))
}
const fullRoles = ['administrator', 'rostering manager', 'recurring rostering manager'];
const readRoles = ['auditor', 'rostering auditor', 'recurring rostering auditor'];

const checkPermissions = (credentials, requiredRoles, member) => {
    const access = credentials.hasRole(requiredRoles, {office: member?.office, client: member?.default_client, location: member?.default_location}) || credentials.memberID == member?.id;
    return (access && !member?.inactive) 
}

export const canUpdateMemberAvailability = (credentials, member) => checkPermissions(credentials, fullRoles, member);
export const canCreateMemberAvailability = canUpdateMemberAvailability
export const canRemoveMemberAvailability = canUpdateMemberAvailability;
export const canViewMemberAvailability = (credentials, member) => checkPermissions(credentials, fullRoles.concat(readRoles), member);
export const canRouteAvailability = ({credentials}) => credentials.hasRole(fullRoles.concat(readRoles));
export const canRouteStaffAvailability = ({credentials}) => canRouteAvailability({credentials}) || credentials?.hasRole('member');

const options = {
    cleanup: { root: availabilityMgrSlice.name, all: state => Object.values(selectAvailabilityData(state)), removeAll: availabilityMgrSlice.actions.remove, key },
    condition: credentials => credentials.hasRole(fullRoles.concat(readRoles))
}

const availabilityInitByID = initMapFactory(fetchAvailability, listen, { name: 'availabilityByID' });
registerEntityBasedFetch('availability', availabilityInitByID);

export const [useMgrAvailability] = dataHookWithCredentialsFactory(selectAvailability, availabilityInitByID, { ...options, defaultValue: null});
export const [useMgrAvailabilities] = dataHookWithCredentialsFactory(selectAvailabilities, initMultiMapFactory(availabilityInitByID, listen), options);
export const [useMgrAvailabilityInRange] = dataHookWithCredentialsFactory(selectAvailabilityInRange, queryMapFactory(fetchAvailabilitiesInRange, 'id', availabilityInitByID, listen), options);
export const [useMgrMemberAvailabilityInRange] = dataHookWithCredentialsFactory(selectMemberAvailabilityInRange, queryMapFactory(fetchMemberAvailabilitiesInRange, 'id', availabilityInitByID, listen), options);

const reducer = { name: availabilityMgrSlice.name, reducer: availabilityMgrSlice.reducer };
export default reducer;